/** Sdílené kousky modulů Objednávky a Zákazníci (sekce Obchod). */
import { KATEGORIE, popisObjednavky, jeHotova } from './stavy';

export const SECTION_COLOR = '#10B981';
export const SECTION_BG = 'rgba(16,185,129,0.12)';
// Norma §3: barvu nesou jen dva stavy (dobře / problém). Výjimka je slib:
// doručení v toleranci je oranžové (SLIBTOL 5 min, u časovky SLIBTOL_CAS 15 min)
export const ZELENA = '#059669';
export const CERVENA = '#EF4444';
export const ORANZOVA = '#F59E0B';
// Záloha, když server toleranci u řádku nepošle (SLIBTOL v číselníku)
export const TOLERANCE_MIN = 5;
// Výkon firmy: rozvoz do 60 minut od objednání (časovky se nepočítají).
// Od VAROVANI_60 se běžící rozvoz ukáže v „K dořešení", ať je čas zasáhnout.
export const LIMIT_60 = 60;
export const VAROVANI_60 = 45;

/** Barva výsledku proti slibu: ok bez barvy, tolerance oranžově, pozdě červeně. */
export const barvaSlibu = (stav) => (stav === 'pozde' ? CERVENA : stav === 'tolerance' ? ORANZOVA : null);

/** Barva podílu v procentech (KPI): ≥ 90 dobře, ≥ 75 pozor, jinak problém. */
export const barvaPodilu = (pct) => (pct == null ? null : pct >= 90 ? ZELENA : pct >= 75 ? ORANZOVA : CERVENA);

export const KANALY = { rozvoz: 'Rozvoz', bistro: 'Bistro', vydej: 'Výdej' };

export const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;
export const hlavicky = () => ({ 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': csrf() });

export function fmtKc(n) {
    if (n == null || Number.isNaN(Number(n))) return '—';
    return `${Math.round(Number(n)).toLocaleString('cs-CZ')} Kč`;
}

/** „2026-10-01 12:14:00" (místní čas z DB) → „12:14" */
export const fmtCas = (s) => (s ? String(s).slice(11, 16) : '');

/** „2026-10-01" → „čt 1. 10." */
export function fmtDen(d, sRokem = false) {
    if (!d) return '—';
    const [y, m, dd] = String(d).slice(0, 10).split('-').map(Number);
    const dt = new Date(y, m - 1, dd);
    const den = dt.toLocaleDateString('cs-CZ', { weekday: 'short' });
    return `${den} ${dd}. ${m}.${sRokem ? ` ${y}` : ''}`;
}

export const fmtDatum = (d) => {
    if (!d) return '—';
    const [y, m, dd] = String(d).slice(0, 10).split('-').map(Number);
    return `${dd}. ${m}. ${y}`;
};

export function platbaPopis(typ) {
    const t = String(typ || '').toLowerCase();
    if (!t) return '—';
    if (t.includes('hotov') || t.includes('cash')) return 'Hotově';
    if (t.includes('kart') || t.includes('card') || t.includes('termin')) return 'Kartou';
    if (t.includes('online') || t.includes('internet') || t.includes('převod')) return 'Online';
    return typ;
}

/** „29.04.2026 13:01:40" (historie stavů z Objedname) → ms */
export function casHistorie(s) {
    const m = /^(\d{2})\.(\d{2})\.(\d{4})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(String(s || '').trim());
    if (!m) return null;
    return new Date(+m[3], +m[2] - 1, +m[1], +m[4], +m[5], +(m[6] || 0)).getTime();
}

const historie = (o) => (Array.isArray(o?.historie_stavu) ? o.historie_stavu : Object.values(o?.historie_stavu || {}));

/** Od kdy je objednávka v aktuálním stavu (ms), nebo čas objednání. */
export function stavOd(o) {
    const cil = String(o?.stav || '').toLowerCase().trim();
    const h = historie(o);
    for (let i = h.length - 1; i >= 0; i--) {
        if (String(h[i]?.stav || '').toLowerCase().trim() === cil) return casHistorie(h[i].datum);
    }
    return o?.cas ? new Date(String(o.cas).replace(' ', 'T')).getTime() : null;
}

export const minut = (odMs, now) => (odMs ? Math.max(0, Math.floor((now - odMs) / 60000)) : null);

export function fmtMinut(m) {
    if (m == null) return '';
    if (m < 60) return `${m} min`;
    return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')}`;
}

/** Slíbený čas rozvozu jako ms dne objednávky (nebo null). */
export function slibMs(o) {
    const m = /^(\d{1,2}):(\d{2})/.exec(String(o?.cas_doruceni_slibeny || ''));
    if (!m || !o?.datum) return null;
    const [y, mo, d] = String(o.datum).slice(0, 10).split('-').map(Number);
    return new Date(y, mo - 1, d, +m[1], +m[2]).getTime();
}

/** Rozvoz, který ještě běží a už je po slíbeném čase (+ tolerance, u časovky volnější). */
export function jePoSlibu(o, now) {
    if (o?.kanal !== 'rozvoz' || o?.kategorie !== 'rozpracovana') return false;
    const s = slibMs(o);
    return s != null && now > s + (o.tolerance_slibu ?? TOLERANCE_MIN) * 60000;
}

/** Kolik minut běží rozvoz, který se měří do 60 (ne časovka); jinak null. */
export function minutRozvozu(o, now) {
    if (o?.kanal !== 'rozvoz' || o?.kategorie !== 'rozpracovana' || o?.casovka) return null;
    const od = o.mereno_od || o.cas;
    return od ? minut(new Date(String(od).replace(' ', 'T')).getTime(), now) : null;
}

const kdo = (o) => o?.zakaznik?.jmeno || o?.pos_nazev_uctu || 'bez jména';

/**
 * „K dořešení" dnešních objednávek — jeden zdroj pro třetí sloupec i mobilní
 * modal (norma §1g). `problem` = červeně (jednoznačný problém), jinak neutrálně.
 */
export function kDoreseniObjednavek(seznam = [], now = Date.now()) {
    const vysledek = [];
    for (const o of seznam) {
        const cislo = o.cislo_denni || o.cislo_objednavky;
        const kdoVeze = o.rozvozce ? ` · ${o.rozvozce}` : '';
        const slib = o.cas_doruceni_slibeny ? `slíbeno ${o.cas_doruceni_slibeny.slice(0, 5)}` : null;
        // Běžící rozvoz: dva limity — slib (zákazník) a 60 minut (firma).
        // Jedna položka na objednávku, ať se v seznamu nezdvojuje.
        const bezi = minutRozvozu(o, now);
        const poSlibu = jePoSlibu(o, now);
        const pres60 = bezi != null && bezi > LIMIT_60;
        if (poSlibu || pres60) {
            vysledek.push({
                id: `pozde-${o.id}`, objednavkaId: o.id, problem: true,
                titulek: `#${cislo} ${kdo(o)} — ${[poSlibu && 'po slíbeném čase', pres60 && `běží ${bezi} min`].filter(Boolean).join(' · ')}`,
                popis: `${[slib, popisObjednavky(o)].filter(Boolean).join(' · ')}${kdoVeze}`,
            });
        } else if (bezi != null && bezi >= VAROVANI_60) {
            vysledek.push({
                id: `blizi60-${o.id}`, objednavkaId: o.id, problem: false,
                titulek: `#${cislo} ${kdo(o)} — běží ${bezi} min, blíží se 60`,
                popis: `${[slib, popisObjednavky(o)].filter(Boolean).join(' · ')}${kdoVeze}`,
            });
        } else if (o.kategorie === 'hotova' && o.vcasnost) {
            const v = o.vcasnost;
            const vady = [
                v.slibeny_stav === 'pozde' && `o ${v.slibeny_rozdil} min po slibu`,
                v.do60_ok === false && `za ${v.minut_celkem} min`,
            ].filter(Boolean);
            if (vady.length) {
                vysledek.push({
                    id: `dorucenopozde-${o.id}`, objednavkaId: o.id, problem: false,
                    titulek: `#${cislo} ${kdo(o)} — doručeno ${vady.join(', ')}`,
                    popis: `${slib || 'bez slibu'}${kdoVeze}`,
                });
            }
        }
        if (o.kategorie === 'ceka_platba' && minut(stavOd(o), now) >= 15) {
            vysledek.push({
                id: `platba-${o.id}`, objednavkaId: o.id, problem: false,
                titulek: `#${cislo} ${kdo(o)} — čeká na platbu ${fmtMinut(minut(stavOd(o), now))}`,
                popis: `${fmtKc(o.celkem_s_dph)} · ${KANALY[o.kanal] || o.kanal}`,
            });
        }
        if (o.kategorie === 'otevrena' && minut(stavOd(o), now) >= 120) {
            vysledek.push({
                id: `ucet-${o.id}`, objednavkaId: o.id, problem: false,
                titulek: `Účet ${o.pos_nazev_uctu || `#${cislo}`} otevřený ${fmtMinut(minut(stavOd(o), now))}`,
                popis: fmtKc(o.celkem_s_dph),
            });
        }
        if (o.kategorie === 'storno' && !/upraven/i.test(o.stav || '')) {
            vysledek.push({
                id: `storno-${o.id}`, objednavkaId: o.id, problem: false,
                titulek: `Storno #${cislo} ${kdo(o)}`,
                popis: `${fmtKc(o.celkem_s_dph)} · ${fmtCas(o.cas)}`,
            });
        }
    }
    // Problémy nahoru
    return vysledek.sort((a, b) => Number(b.problem) - Number(a.problem));
}

/**
 * Stav objednávky jako pilulka: červeně storno, jinak neutrálně. Hotová je
 * výchozí stav skoro každého řádku — zelená by byla na všech a přestala by
 * cokoli říkat (norma §3: barva jen na to, co je potřeba vidět).
 */
export function StavOdznak({ o, isDark, maly = false }) {
    const k = o?.kategorie;
    const barva = k === 'storno' ? CERVENA : null;
    const text = k === 'storno' || jeHotova(o) ? KATEGORIE[k === 'storno' ? 'storno' : 'hotova'].label : popisObjednavky(o);
    return (
        <span title={o?.stav || ''} style={{
            fontSize: maly ? 10.5 : 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap',
            display: 'inline-block', lineHeight: '16px', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis',
            color: barva || 'var(--text-secondary)',
            background: barva ? `${barva}18` : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.045)'),
            border: `1px solid ${barva ? `${barva}33` : (isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)')}`,
        }}>{text}</span>
    );
}

/** Nadpis sekce v panelu / detailu (kapitálky, jako všude v normě). */
export function Nadpis({ children, muted, style }) {
    return (
        <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: '0 0 6px', ...style }}>
            {children}
        </p>
    );
}

/** Řádek panelu `popis · hodnota`. */
export function RadekPanelu({ popis, hodnota, cervene = false, muted, cara }) {
    return (
        <div className="flex justify-between text-[13px]" style={{ minHeight: 30, alignItems: 'center', gap: 10, borderBottom: `1px solid ${cara}` }}>
            <span style={{ color: muted }}>{popis}</span>
            <b style={{ color: cervene ? '#F87171' : 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{hodnota}</b>
        </div>
    );
}

const fmtPct = (p) => (p == null ? '—' : `${Math.round(p)} %`);
const fmtZ = (k) => (k?.z ? `${k.ok} z ${k.z}` : 'zatím nic');

/**
 * Hlavní čísla dne / období v jednom pruhu: tržby · objednávky · do 60 min ·
 * proti slibu. Na telefonu nahoře nad seznamem (třetí sloupec tam není),
 * na desktopu v panelu. Do 60 min = výkon firmy (bez časovek), proti slibu
 * = férovost k zákazníkovi — proto dvě čísla, ne jedno.
 */
export function KpiPruh({ stats, zive, muted, isDark }) {
    const d = stats?.doruceni;
    // Podrobnosti (x z y, srovnání) jsou jen v tooltipu — pruh má být čitelný na první pohled
    const dlazdice = [
        { k: 'trzby', popis: 'Tržba', hodnota: stats ? fmtKc(stats.trzby) : '—' },
        { k: 'pocet', popis: 'Počet', hodnota: stats ? String(stats.pocet) : '—', titulek: stats?.pocet ? `ø ${fmtKc(stats.prumer)} na objednávku` : undefined },
        {
            k: 'do60', popis: 'Do 60min', hodnota: fmtPct(d?.do60?.pct), barva: barvaPodilu(d?.do60?.pct),
            titulek: `${fmtZ(d?.do60)} — rozvozy doručené do 60 minut od objednání${d?.casovek ? `; ${d.casovek} časovek se nepočítá` : ''}`,
        },
        {
            k: 'slib', popis: 'Slib', hodnota: fmtPct(d?.slib?.pct), barva: barvaPodilu(d?.slib?.pct),
            titulek: `${fmtZ(d?.slib)} — doručeno do slíbeného času + tolerance (5 min, časovka 15 min)`,
        },
    ];
    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.5fr) repeat(3, minmax(0,1fr))', gap: 8 }}>
            {dlazdice.map(t => (
                <div key={t.k} title={t.titulek} className="min-w-0" style={{
                    padding: '7px 8px', borderRadius: 9,
                    background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'var(--border-subtle)'}`,
                }}>
                    <span className="truncate" style={{ display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase', color: muted }}>{t.popis}</span>
                    <span className="truncate" style={{ display: 'block', fontSize: 16, fontWeight: 800, lineHeight: 1.3, color: t.barva || 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{t.hodnota}</span>
                </div>
            ))}
        </div>
    );
}

/** Blok „Doručení" v panelu (desktop): oba ukazatele včasnosti s rozpadem. */
export function DoruceniBlok({ stats, zive, muted, cara }) {
    const d = stats?.doruceni;
    if (!d) return null;
    const p = d.predchozi;
    const srovnani = (ted, pred, jednotka = ' %', menePlus = false) => {
        if (ted == null || pred == null) return null;
        const r = Math.round(ted - pred);
        if (!r) return 'jako minule';
        const lepsi = menePlus ? r < 0 : r > 0;
        return <span style={{ color: lepsi ? ZELENA : CERVENA }}>{r > 0 ? '+' : '−'}{Math.abs(r)}{jednotka}</span>;
    };
    const Radek = ({ popis, hodnota, barva, pod, vedle }) => (
        <div style={{ padding: '6px 0', borderBottom: `1px solid ${cara}` }}>
            <div className="flex justify-between items-baseline" style={{ gap: 10 }}>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{popis}</span>
                <b style={{ fontSize: 15, color: barva || 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{hodnota}</b>
            </div>
            <div className="flex justify-between" style={{ gap: 10, fontSize: 11.5, color: muted, fontVariantNumeric: 'tabular-nums' }}>
                <span>{pod}</span>{vedle && <span>{vedle}</span>}
            </div>
        </div>
    );
    return (
        <>
            <Nadpis muted={muted} style={{ marginTop: 16 }}>Doručení rozvozu</Nadpis>
            <Radek popis="Do 60 minut" hodnota={fmtPct(d.do60.pct)} barva={barvaPodilu(d.do60.pct)}
                pod={d.do60.z ? `${d.do60.ok} z ${d.do60.z} · výkon firmy` : 'zatím žádný doručený rozvoz'}
                vedle={zive && p ? srovnani(d.do60.pct, p.do60) : null} />
            <Radek popis="Proti slibu" hodnota={fmtPct(d.slib.pct)} barva={barvaPodilu(d.slib.pct)}
                pod={d.slib.z ? `${d.slib.ok} z ${d.slib.z} · férovost k zákazníkovi` : 'bez slíbeného času'}
                vedle={zive && p ? srovnani(d.slib.pct, p.slib) : null} />
            <Radek popis="Průměrná doba" hodnota={d.prumer_minut != null ? `${Math.round(d.prumer_minut)} min` : '—'}
                pod={d.casovek ? `bez ${d.casovek} časovek` : 'od objednání po doručení'}
                vedle={zive && p ? srovnani(d.prumer_minut, p.prumer_minut, ' min', true) : null} />
            {zive && p && <p style={{ margin: '4px 0 0', fontSize: 11, color: muted }}>srovnání se stejným dnem minulý týden</p>}
        </>
    );
}

/** Sparkline bez os (norma §3): jedna barva, plocha, zvýrazněný konec. */
export function Prubeh({ body = [], barva = SECTION_COLOR, vyska = 44 }) {
    if (body.length < 2) return null;
    const sirka = 280;
    const max = Math.max(...body, 1);
    const x = (i) => (i / (body.length - 1)) * sirka;
    const y = (v) => vyska - 3 - (v / max) * (vyska - 8);
    const cesta = body.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ');
    return (
        <svg viewBox={`0 0 ${sirka} ${vyska}`} preserveAspectRatio="none" style={{ width: '100%', height: vyska, display: 'block' }} aria-hidden="true">
            <path d={`${cesta} L${sirka} ${vyska} L0 ${vyska}Z`} fill={`${barva}22`} />
            <path d={cesta} fill="none" stroke={barva} strokeWidth="1.6" vectorEffect="non-scaling-stroke" />
            <circle cx={x(body.length - 1)} cy={y(body[body.length - 1])} r="2.6" fill={barva} />
        </svg>
    );
}
